import { FC, useEffect, useMemo, useRef } from 'react'
import ProductsSlider, {
  ProductSlide
} from '@/components/organisms/productsSlider/index'
import { Box, Flex } from '@/components/atoms/Grid'
import { validFor } from '@/theme/validFor'
import styled from 'styled-components'
import { Body3, H1 } from '@/components/atoms/typography'
import useTranslation from 'next-translate/useTranslation'
import { Translate } from 'next-translate'
import {
  Desktop,
  MediaContextProvider,
  Mobile,
  Tablet
} from '@/components/atoms/media'
import { BridgeProduct } from 'types/bridgeProduct'
import ProductCard, {
  ProductCardActionType
} from '@/components/organisms/productCard/productCard'
import Spinner from '@/components/molecules/spinner'
import useSWRImmutable from 'swr/immutable'
import {
  withTrackVisibility,
  WithTrackVisibilityProps
} from '@/HOC/withTrackVisibility'
import { Fetcher } from 'swr'
import ProductCardPlaceholder, {
  ProductCardColors
} from '@/components/atoms/productCardPlaceholder'
import { RecommendedIconDefault } from '@/components/atoms/icons/RecommendedIconDefault'
import { BestSellerIconDefault } from '@/components/atoms/icons/BestSellerIconDefault'

import { ProductCardMode } from '@/components/organisms/productCard/productCardMode'

export enum TitleType {
  recommended,
  bestSeller,
  similar,
  youMayLike,
  beInspired,
  simple,
  custom,
  empty,
  lastSeen,
  similarFC,
  categoryFC,
  popularOfBrands,
  lastSeenFC,
  bestsellersWomen,
  bestsellersMen,
  bestsellersChildren,
  newsWomen,
  newsMen,
  newsChildren
}

export const SliderPosition = {
  first: 'first',
  second: 'second',
  third: 'third',
  fourth: 'fourth',
  fifth: 'fifth'
}

export enum CustomType {
  recommended = 'recommended',
  bestSeller = 'bestsellers',
  similar = 'similar_product',
  youMayLike = 'recommended',
  beInspired = 'beInspired',
  simple = 'simple',
  custom = 'custom',
  empty = 'empty',
  lastSeen = 'last_seen',
  similarFC = 'similarFC',
  categoryFC = 'similarFC',

  popularOfBrands = 'popularOfBrands',
  lastSeenFC = 'lastSeen',
  insale = 'inSale'
}

export interface SlideItemsCount {
  mobile: number
  tablet: number
  desktop: number
}

type ListType =
  | string
  | string[]
  | {
      personalizedList?: string
      similarList: string
      categoryList: string
      lastSeenList: string
      popularOfBrands?: string
    }

type ProductsType =
  | BridgeProduct[]
  | {
      personalizedProducts?: BridgeProduct[]
      similarProducts?: BridgeProduct[]
      categoryProducts?: BridgeProduct[]
      similarFCProducts?: BridgeProduct[]
      categoryFCProducts?: BridgeProduct[]
      popularOfBrandsProducts?: BridgeProduct[]
      lastSeenProducts?: BridgeProduct[]
      lastSeenFCProducts?: BridgeProduct[]
    }

type AlgorithmType =
  | string
  | string[]
  | {
      personalizedAlgorithm?: string
      similarAlgorithm: string
      categoryAlgorithm: string
      lastSeenAlgorithm: string
      popularOfBrands?: string
    }

export type HorizontalTitledProductsSliderFetcher = {
  list: ListType
  products: ProductsType
  algorithm: AlgorithmType
}
export const getMhubListPrefix = (
  pageCode: string,
  recommderCode: CustomType,
  algorithm: string
): string => {
  return pageCode + '.*position*.' + recommderCode + '.' + algorithm
}

type HorizontalTitledProductsSliderProps = {
  titleType: TitleType
  customTitle?: JSX.Element
  simpleTitle?: string
  simpleSubTitle?: string
  simpleIcon?: JSX.Element
  slideItemsCount: SlideItemsCount
  spaceBetween?: number
  arrowsTopPercent?: number
  arrowsBottomMargin?: number
  fetcher: Fetcher<HorizontalTitledProductsSliderFetcher>
  swrKey: string | unknown[]
  action: ProductCardActionType
  loadImmediately?: boolean
  setHidden?: (hidden: boolean) => void
  useCache?: boolean
  colors?: ProductCardColors
  indentText?: boolean
  showTitle?: boolean
  productsFromFetcher?: BridgeProduct[]
  position?: string
  productList?: ListType
  productBrand?: string
  hideIcon?: boolean
} & WithTrackVisibilityProps &
  Rest

const HorizontalTitledProductsSlider: FC<HorizontalTitledProductsSliderProps> =
  ({
    titleType,
    customTitle,
    simpleTitle,
    simpleSubTitle,
    simpleIcon,
    slideItemsCount,
    arrowsTopPercent,
    arrowsBottomMargin = 64,
    fetcher,
    swrKey,
    action,
    loadImmediately = true,
    isVisible,
    setHidden,
    useCache = true,
    colors,
    indentText,
    showTitle = true,
    productsFromFetcher,
    position,
    productList,
    productBrand,
    hideIcon = false,
    ...rest
  }) => {
    const timestamp = useRef<number>(Date.now())
    const viewportRef = useRef<HTMLDivElement>()
    const { t } = useTranslation('common')

    const title = mapTitle(
      titleType,
      t,
      simpleTitle,
      simpleSubTitle,
      simpleIcon,
      customTitle,
      hideIcon,
      productBrand
    )
    const getNoCachedSwrKey = () => {
      if (!swrKey) {
        return null
      }
      return Array.isArray(swrKey)
        ? [...swrKey, timestamp.current]
        : [swrKey, timestamp.current]
    }

    const { data, isValidating } = useSWRImmutable(
      (loadImmediately || isVisible) && swrKey
        ? !useCache
          ? getNoCachedSwrKey()
          : swrKey
        : null,
      fetcher
    )

    const { products, list } = data ?? {}

    const getMhubList = (): string => {
      const listData = productList ? productList : list
      let value = ''

      if (typeof listData === 'string') {
        value = listData
      } else if (Array.isArray(listData)) {
        value = listData[0] ?? ''
      }

      if (value.includes('*position*')) {
        value = value.replace('*position*', position)
      }

      return value
    }

    const renderProductCard = (products: BridgeProduct[]) => {
      const mhubList = getMhubList()
      return products.map((product: BridgeProduct, offset: number) => (
        <ProductSlide key={offset}>
          <ProductCard
            onAddToCart={action}
            product={product}
            position={offset + 1}
            mode={ProductCardMode.recommended}
            virtualScrolling={false}
            list={mhubList}
            colors={colors}
            indentText={indentText}
          />
        </ProductSlide>
      ))
    }

    const Products = Array.isArray(products)
      ? renderProductCard(products)
      : productsFromFetcher && productsFromFetcher.length > 0
      ? renderProductCard(productsFromFetcher)
      : []

    const Placeholders = (itemsCount: number) =>
      Array.from(Array(Math.ceil(itemsCount)).keys()).map((_, offset) => (
        <ProductSlide key={offset}>
          <ProductCardPlaceholder colors={colors} />
        </ProductSlide>
      ))

    const Items = (itemsCount: number) =>
      swrKey && !isValidating ? Products : Placeholders(itemsCount)

    const Slider = (itemsCount: number) => (
      <ProductsSlider
        slidesToShow={itemsCount}
        slidesPerGroup={Math.floor(itemsCount)}
        spaceBetween={10}
        arrowsTopPercent={arrowsTopPercent}
        arrowsBottomMargin={arrowsBottomMargin}
      >
        {Items(itemsCount)}
      </ProductsSlider>
    )

    const PlaceholderOrSlider = (itemsCount: number) => (
      <>{!isHidden && Slider(itemsCount)}</>
    )

    const isHidden = useMemo(() => {
      if (swrKey) {
        if (Array.isArray(products)) {
          return !(isValidating || products.length > 0)
        } else if (typeof products === 'object') {
          return !(
            isValidating ||
            products.personalizedProducts.length > 0 ||
            products.similarProducts.length > 0 ||
            products.categoryProducts.length > 0 ||
            products.lastSeenProducts.length > 0
          )
        }
      }
      return false
    }, [isValidating, products, swrKey])

    useEffect(() => {
      setHidden && setHidden(isHidden)
    }, [isHidden])

    return (
      <>
        {!isHidden && (
          <StyledWrapper {...rest}>
            {showTitle && title}
            <Box
              position="relative"
              ref={viewportRef}
              data-testid="recomProducts"
            >
              {isValidating && (
                <SpinnerContainer>
                  <Spinner />
                </SpinnerContainer>
              )}
              <MediaContextProvider>
                <Desktop>
                  {PlaceholderOrSlider(slideItemsCount.desktop + 0.5)}
                </Desktop>
                <Tablet>
                  {PlaceholderOrSlider(slideItemsCount.tablet + 0.5)}
                </Tablet>
                <Mobile>
                  {PlaceholderOrSlider(slideItemsCount.mobile + 0.5)}
                </Mobile>
              </MediaContextProvider>
            </Box>
          </StyledWrapper>
        )}
      </>
    )
  }

export default withTrackVisibility(HorizontalTitledProductsSlider)

const mapTitle = (
  titleType: TitleType,
  t: Translate,
  simpleTitle: string,
  simpleSubTitle: string,
  simpleIcon: JSX.Element,
  customTitle: JSX.Element,
  hideIcon: boolean,
  productBrand?: string
): JSX.Element => {
  const defaultTitle = (
    title: string,
    subTitle: string,
    IconElement: () => JSX.Element,
    hideIcon?: boolean
  ) => (
    <StyledSectionFlex>
      <StyledH1>{title}</StyledH1>
      {!hideIcon && (
        <IconTextWrapper>
          {subTitle && <IconElement />}

          <StyledBodyText mx="15px">{subTitle}</StyledBodyText>
        </IconTextWrapper>
      )}
    </StyledSectionFlex>
  )

  const defaultTitleNew = (title: string, subTitle?: string) => (
    <StyledSectionFlexNew>
      <StyledH1>{title}</StyledH1>
      <p>{subTitle}</p>
    </StyledSectionFlexNew>
  )

  switch (titleType) {
    case TitleType.recommended:
      return defaultTitle(
        t('HomePage.Sections.Recommended.title'),
        t('HomePage.Sections.Recommended.subTitle'),
        () => <RecommendedIconDefault />,
        hideIcon
      )
    case TitleType.bestSeller:
      return defaultTitle(
        t('HomePage.Sections.TopSellers.title'),
        t('HomePage.Sections.TopSellers.subTitle'),
        () => <BestSellerIconDefault />,
        hideIcon
      )
    case TitleType.lastSeen:
      return defaultTitle(
        t('HomePage.Sections.LastSeen.title'),
        t('HomePage.Sections.LastSeen.subTitle'),
        () => <BestSellerIconDefault />,
        hideIcon
      )
    case TitleType.similar:
      return defaultTitle(
        t('HomePage.Sections.Similar.title'),
        t('HomePage.Sections.Similar.subTitle'),
        () => <BestSellerIconDefault />
      )
    case TitleType.youMayLike:
      return defaultTitle(
        t('HomePage.Sections.YouMayLike.title'),
        t('HomePage.Sections.YouMayLike.subTitle'),
        () => <BestSellerIconDefault />
      )
    case TitleType.beInspired:
      return defaultTitle(
        t('ProductDetail.GenderTagProductSlider.title'),
        t('ProductDetail.GenderTagProductSlider.subTitle'),
        () => <BestSellerIconDefault />
      )
    case TitleType.similarFC:
      return defaultTitleNew(
        t('ProductDetail.SimilarFCProductSlider.title'),
        t('ProductDetail.SimilarFCProductSlider.subTitle')
      )

    case TitleType.categoryFC:
      return defaultTitleNew(
        t('ProductDetail.CategoryFCProductSlider.title'),
        t('ProductDetail.CategoryFCProductSlider.subTitle')
      )

    case TitleType.popularOfBrands:
      return defaultTitleNew(
        t('ProductDetail.PopularOfBrandsProductSlider.title') +
          ' ' +
          productBrand,
        t('ProductDetail.PopularOfBrandsProductSlider.subTitle')
      )

    case TitleType.lastSeenFC:
      return defaultTitleNew(
        t('HomePage.Sections.LastSeen.title'),
        t('ProductDetail.LastSeenFCProductSlider.subTitle')
      )

    case TitleType.bestsellersWomen:
    case TitleType.bestsellersMen:
    case TitleType.bestsellersChildren:
    case TitleType.newsWomen:
    case TitleType.newsMen:
    case TitleType.newsChildren: {
      const key = TitleType[titleType]
      const translationKey = `HomePage.Sections.${key}.title`
      return defaultTitleNew(t(translationKey))
    }

    case TitleType.simple:
      return defaultTitle(simpleTitle, simpleSubTitle, () => (
        <BestSellerIconDefault />
      ))
    case TitleType.custom:
      return customTitle
    case TitleType.empty:
      return null
  }
}

const StyledSectionFlexNew = styled(Flex)`
  flex-direction: column;
  letter-spacing: 0;

  h1 {
    font-size: 24px;
    margin-top: 32px;
    margin-bottom: 8px;
  }
  p {
    font-size: 16px;
    margin-top: 0px;
    margin-bottom: 24px;
  }

  ${validFor.mobile`
    h1 {
      margin-top: 32px;
      margin-bottom: 8px;
    }
    p {
      font-size: 12px;
      margin-top: 0px;
      margin-bottom: 16px;
    }
  `}
`

const StyledSectionFlex = styled(Flex)`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin: 30px 0 0 0;
  ${validFor.tablet`
     margin: 20px 0 0 0;
  `}
  ${validFor.mobile`
     margin: 15px 0 0 0;
  `}
`

const StyledH1 = styled(H1)`
  ${validFor.tablet`
    font-size: 30px;
  `}
  ${validFor.mobile`
    font-size: 20px;
  `}
`

const StyledWrapper = styled(Flex)`
  position: relative;
  flex-direction: column;
  width: 100%;
  padding: 0;
`

const IconTextWrapper = styled(Flex)`
  flex-direction: row;
  justify-content: center;
  align-items: center;
  ${validFor.mobile`
    display: none;
  `}
`

const StyledBodyText = styled(Body3)`
  opacity: 0.5;
  ${validFor.tablet`
    font-size: 14px;
  `}
`

const SpinnerContainer = styled(Flex)`
  position: absolute;
  z-index: 10;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  align-items: center;
  justify-content: center;
`
